import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LuEye, LuEyeClosed } from "react-icons/lu";
import { FaArrowRight } from "react-icons/fa";
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCPFCNPJ, unformat } from '../../../utils/formatters';
import { validateDocument } from '../../../utils/validators';
import { destacarPendencias } from '../../../utils/destaqueCampos';

const ErrorMessage = styled.div`
  background: #fee;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.2rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '⚠️';
    font-size: 1rem;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 0.7rem 0.9rem;
  }
`;

const SuccessMessage = styled.div`
  background: #e8f7ef;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.2rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '✅';
    font-size: 1rem;
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 0.7rem 0.9rem;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.darkBlue};
  margin-bottom: 0.5rem;
  text-align: center;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.3rem;
  }
`;

const FormSubtitle = styled.p`
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
    margin-bottom: 1.5rem;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1.2rem;
  box-sizing: border-box;
  scroll-margin-top: 6rem;
`;

const CampoErro = styled.small`
  display: block;
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 0.3rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${colors.darkBlue};
  font-weight: 600;
  font-size: 0.9rem;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.9rem;
  border: 2px solid ${props => props.$hasError ? '#f5c6cb' : '#e8ecef'};
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  background: ${colors.backgroundLight};

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#e74c3c' : colors.lightBlue};
    background: white;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0.8rem;
    font-size: 16px;
  }
`;

const PasswordContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PasswordInput = styled(Input)`
  padding-right: 3rem;
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  // 40x40 de área de toque; o ícone continua no mesmo lugar
  right: 0.3rem;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.2rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${colors.darkBlue};
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.disabled ? '#cccccc' : colors.darkBlue};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    background: #0a1a45;
    transform: translateY(-2px);
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0.9rem;
    font-size: 0.95rem;
  }
`;

const ToggleLink = styled.div`
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-top: 1rem;

  button {
    color: ${colors.darkBlue};
    background: none;
    border: none;
    text-decoration: none;
    font-weight: 600;
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0.2rem 0.4rem;
    transition: all 0.2s ease;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;

    button {
      font-size: 0.85rem;
    }
  }
`;

const ForgotPasswordLink = styled.div`
  text-align: center;
  margin: 1rem 0 1.5rem 0;

  a {
    color: ${colors.darkBlue};
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    display: inline-block;
    padding: 0.3rem;

    &:hover {
      text-decoration: underline;
      color: #0a1a45;
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    margin: 0.8rem 0 1.2rem 0;

    a {
      font-size: 0.85rem;
    }
  }
`;

const LoginForm = ({ onToggleForm }) => {
  const { login, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Se a pessoa caiu aqui por causa de um link direto sem sessão ativa (ver
  // ProtectedRoute em App.js), volta pra essa URL depois do login em vez de
  // sempre mandar pra /home. Só existe se ela veio de uma rota protegida —
  // acesso direto ao /login (sem link nenhum) continua caindo em /home.
  const destinoAposLogin = location.state?.from
    ? `${location.state.from.pathname}${location.state.from.search || ''}${location.state.from.hash || ''}`
    : '/home';
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ cpfCnpj: '', password: '' });
  const [localError, setLocalError] = useState('');
  // Erros de validação por campo (vazio / documento inválido), exibidos embaixo
  // de cada input; `localError` fica pros erros do servidor (credenciais etc.).
  const [camposErro, setCamposErro] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    if (localError) setLocalError('');
    if (camposErro[id]) setCamposErro(prev => ({ ...prev, [id]: null }));
  };

  const handleCpfChange = (e) => {
    const formattedValue = formatCPFCNPJ(e.target.value);
    setFormData(prev => ({ ...prev, cpfCnpj: formattedValue }));
    if (camposErro.cpfCnpj) setCamposErro(prev => ({ ...prev, cpfCnpj: null }));
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const formatErrorMessage = (errorObj) => {
    if (!errorObj) return '';
    if (typeof errorObj === 'string') return errorObj;
    if (errorObj.message) return errorObj.message;
    if (errorObj.error) return errorObj.error;
    return 'Erro desconhecido';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const documentoNumeros = unformat(formData.cpfCnpj);

    const novosErros = {};
    if (!documentoNumeros) {
      novosErros.cpfCnpj = 'Informe seu CPF ou CNPJ';
    } else if (!validateDocument(documentoNumeros)) {
      novosErros.cpfCnpj = `${documentoNumeros.length === 11 ? 'CPF' : 'CNPJ'} inválido`;
    }
    if (!formData.password.trim()) {
      novosErros.password = 'Informe sua senha';
    }

    setCamposErro(novosErros);
    if (Object.keys(novosErros).length > 0) {
      setLocalError('');
      destacarPendencias(Object.keys(novosErros));
      return;
    }

    setLocalError('');
    setSuccessMessage('');

    try {
      const result = await login(documentoNumeros, formData.password);

      if (result.success) {
        setSuccessMessage('Login realizado com sucesso!');
        // Delay para o usuário ver a mensagem de sucesso antes do redirecionamento
        setTimeout(() => {
          navigate(destinoAposLogin);
        }, 1000);
      } else {
        setLocalError(result.error || 'Erro ao fazer login');
      }
    } catch (err) {
      setLocalError('Erro ao conectar com o servidor.');
    }
  };

  return (
    <FormContainer>
      <FormTitle>Acesso ao Sistema</FormTitle>
      <FormSubtitle>Entre com suas credenciais</FormSubtitle>

      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      {error && !localError && <ErrorMessage>{formatErrorMessage(error)}</ErrorMessage>}
      {localError && <ErrorMessage>{formatErrorMessage(localError)}</ErrorMessage>}

      <form onSubmit={handleSubmit} noValidate>
        <InputGroup data-field-name="cpfCnpj">
          <Label htmlFor="cpfCnpj">CPF / CNPJ</Label>
          <Input
            type="text"
            id="cpfCnpj"
            placeholder="Digite seu CPF ou CNPJ"
            value={formData.cpfCnpj}
            onChange={handleCpfChange}
            $hasError={!!localError || !!camposErro.cpfCnpj}
            aria-invalid={!!camposErro.cpfCnpj}
            required
            disabled={loading}
          />
          {camposErro.cpfCnpj && <CampoErro>{camposErro.cpfCnpj}</CampoErro>}
        </InputGroup>

        <InputGroup data-field-name="password">
          <Label htmlFor="password">Senha</Label>
          <PasswordContainer>
            <PasswordInput
              type={showPassword ? "text" : "password"}
              id="password"
              placeholder="Digite sua senha"
              value={formData.password}
              onChange={handleChange}
              $hasError={!!localError || !!camposErro.password}
              aria-invalid={!!camposErro.password}
              required
              disabled={loading}
            />
            <TogglePasswordButton
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              disabled={loading}
            >
              {showPassword ? <LuEye size={18} /> : <LuEyeClosed size={18} />}
            </TogglePasswordButton>
          </PasswordContainer>
          {camposErro.password && <CampoErro>{camposErro.password}</CampoErro>}
        </InputGroup>

        <PrimaryButton type="submit" disabled={loading}>
          {loading ? 'Autenticando...' : (
            <>
              Entrar
              <FaArrowRight />
            </>
          )}
        </PrimaryButton>
      </form>

      <ForgotPasswordLink>
        <Link to="/esqueci-senha">Esqueci minha senha</Link>
      </ForgotPasswordLink>

      <ToggleLink>
        Não tem uma conta?{' '}
        <button type="button" onClick={onToggleForm} disabled={loading}>
          Cadastre-se agora
        </button>
      </ToggleLink>
    </FormContainer>
  );
};

export default LoginForm;
